import React, { useEffect, useRef, useState } from 'react';

const RyadAvailability = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [showPDF, setShowPDF] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Data from the PDF - Disponibilités
  const apartments = [
    { floor: 'RDC', code: 'F4-A', type: 'F4', surface: 138, price: 124200000, status: 'disponible' },
    { floor: 'Étage 1', code: 'F2-A', type: 'F2', surface: 43, price: 40000000, status: 'vendu' },
    { floor: 'Étage 1', code: 'F2-B', type: 'F2', surface: 60.47, price: 54423000, status: 'disponible' },
    { floor: 'Étage 1', code: 'F3-A', type: 'F3', surface: 88, price: 78300000, status: 'vendu' },
    { floor: 'Étage 1', code: 'F3-B', type: 'F3', surface: 88, price: 78300000, status: 'disponible' },
    { floor: 'Étage 2', code: 'F2-A', type: 'F2', surface: 43, price: 40000000, status: 'vendu' },
    { floor: 'Étage 2', code: 'F2-B', type: 'F2', surface: 60.47, price: 54423000, status: 'disponible' },
    { floor: 'Étage 2', code: 'F3-A', type: 'F3', surface: 88, price: 78300000, status: 'vendu' },
    { floor: 'Étage 2', code: 'F3-B', type: 'F3', surface: 88, price: 78300000, status: 'disponible' },
    { floor: 'Étage 3', code: 'F2-A', type: 'F2', surface: 43, price: 40000000, status: 'disponible' },
    { floor: 'Étage 3', code: 'F2-B', type: 'F2', surface: 60.47, price: 54423000, status: 'disponible' },
    { floor: 'Étage 3', code: 'F3-A', type: 'F3', surface: 88, price: 78300000, status: 'vendu' },
    { floor: 'Étage 3', code: 'F3-B', type: 'F3', surface: 88, price: 78300000, status: 'vendu' },
    { floor: 'Étage 4', code: 'F4-B', type: 'F4', surface: 141, price: 126900000, status: 'disponible' },
    { floor: 'Étage 4', code: 'F4-C', type: 'F4', surface: 135, price: 121500000, status: 'disponible' },
    { floor: 'Étage 5', code: 'Penthouse', type: 'Penthouse', surface: 250, price: 250000000, status: 'disponible' },
  ];

  const formatPrice = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' FCFA';
  };

  const formatPriceFull = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' FCFA';
  };

  const filteredApartments = selectedType === 'all' 
    ? apartments 
    : apartments.filter(apt => apt.type === selectedType);

  const availableCount = apartments.filter(apt => apt.status === 'disponible').length;
  const soldCount = apartments.filter(apt => apt.status === 'vendu').length;

  const types = ['all', 'F2', 'F3', 'F4', 'Penthouse'];

  const pdfUrl = "https://customer-assets.emergentagent.com/job_site-opener-21/artifacts/ic7ldu1p_DISPONIBILITE%202.pdf";

  const getTypeBadgeColor = (type) => {
    switch(type) {
      case 'Penthouse': return 'bg-purple-100 text-purple-700';
      case 'F4': return 'bg-blue-100 text-blue-700';
      case 'F3': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <section id="disponibilites" ref={sectionRef} className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-0 w-64 h-64 md:w-96 md:h-96 bg-green-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-48 h-48 md:w-80 md:h-80 bg-amber-500/5 rounded-full blur-3xl"></div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-8 md:mb-10 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <span className="inline-block px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold mb-4 tracking-wide uppercase">
            🏠 Disponibilités
          </span>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            <span className="gradient-text">Appartements Disponibles</span>
          </h2>
          <p className="text-sm md:text-base text-gray-600 max-w-xl mx-auto">
            Consultez la liste des appartements disponibles à la Résidence RYAD
          </p>
        </div>

        {/* Stats Cards - Compact */}
        <div className={`grid grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="bg-white rounded-xl p-3 md:p-4 shadow-md border border-gray-100 text-center">
            <div className="text-xl md:text-2xl font-bold text-green-600">{availableCount}</div>
            <div className="text-xs text-gray-500">Dispo.</div>
          </div>
          <div className="bg-white rounded-xl p-3 md:p-4 shadow-md border border-gray-100 text-center">
            <div className="text-xl md:text-2xl font-bold text-red-600">{soldCount}</div>
            <div className="text-xs text-gray-500">Vendus</div>
          </div>
          <div className="bg-white rounded-xl p-3 md:p-4 shadow-md border border-gray-100 text-center">
            <div className="text-xl md:text-2xl font-bold text-gray-800">{apartments.length}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
          <div className="bg-white rounded-xl p-3 md:p-4 shadow-md border border-gray-100 text-center">
            <div className="text-xl md:text-2xl font-bold text-blue-600">6</div>
            <div className="text-xs text-gray-500">Étages</div>
          </div>
        </div>

        {/* Filter Buttons - Compact */}
        <div className={`flex flex-wrap justify-center gap-2 mb-6 transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {types.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 ${
                selectedType === type
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {type === 'all' ? 'Tous' : type}
            </button>
          ))}
        </div>

        {/* Desktop Table - Hidden on Mobile */}
        <div className={`hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {/* Table Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-3">
            <div className="grid grid-cols-5 gap-2 text-xs font-semibold uppercase tracking-wide">
              <div>Étage</div>
              <div>Type</div>
              <div>Surface</div>
              <div>Prix</div>
              <div className="text-center">Statut</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
            {filteredApartments.map((apt, index) => (
              <div 
                key={`${apt.floor}-${apt.code}`}
                className={`grid grid-cols-5 gap-2 px-4 py-2.5 items-center hover:bg-gray-50 transition-colors duration-200 text-sm ${
                  apt.status === 'disponible' ? 'bg-green-50/30' : ''
                }`}
              >
                <div className="font-medium text-gray-700 text-xs">{apt.floor}</div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(apt.type)}`}>
                    {apt.code}
                  </span>
                </div>
                <div className="text-gray-600 text-xs">{apt.surface} m²</div>
                <div className="text-gray-800 font-semibold text-xs">
                  {apt.status === 'disponible' ? formatPrice(apt.price) : '-'}
                </div>
                <div className="text-center">
                  {apt.status === 'disponible' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                      Dispo
                    </span>
                  ) : apt.status === 'vendu' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></span>
                      Vendu
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5"></span>
                      Réservé
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Cards - Visible only on Mobile */}
        <div className={`md:hidden space-y-3 max-h-[500px] overflow-y-auto transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {filteredApartments.map((apt, index) => (
            <div 
              key={`mobile-${apt.floor}-${apt.code}`}
              className={`bg-white rounded-xl p-4 shadow-md border transition-all duration-200 ${
                apt.status === 'disponible' 
                  ? 'border-green-200 bg-green-50/30' 
                  : 'border-gray-100'
              }`}
            >
              {/* Top Row: Type Badge & Status */}
              <div className="flex items-center justify-between mb-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getTypeBadgeColor(apt.type)}`}>
                  {apt.code}
                </span>
                {apt.status === 'disponible' ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                    Disponible
                  </span>
                ) : apt.status === 'vendu' ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1.5"></span>
                    Vendu
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-1.5"></span>
                    Réservé
                  </span>
                )}
              </div>
              
              {/* Info Grid */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-xs text-gray-500 mb-0.5">Étage</div>
                  <div className="text-sm font-semibold text-gray-800">{apt.floor.replace('Étage ', '')}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-xs text-gray-500 mb-0.5">Surface</div>
                  <div className="text-sm font-semibold text-gray-800">{apt.surface} m²</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-xs text-gray-500 mb-0.5">Prix</div>
                  <div className="text-sm font-semibold text-amber-600">
                    {apt.status === 'disponible' ? formatPrice(apt.price) : '-'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* PDF Download Section - Compact */}
        <div className={`mt-8 transform transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-5 md:p-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                Document Officiel
              </h3>
              <p className="text-amber-100 text-sm mb-5 max-w-md mx-auto">
                Téléchargez le PDF avec toutes les informations détaillées.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-white text-amber-600 px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Télécharger PDF
                </a>
                <button
                  onClick={() => setShowPDF(!showPDF)}
                  className="inline-flex items-center justify-center bg-white/20 hover:bg-white/30 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 backdrop-blur-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPDF ? 'Masquer' : 'Voir PDF'}
                </button>
              </div>
            </div>
          </div>

          {/* PDF Viewer */}
          {showPDF && (
            <div className="mt-6 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div className="bg-gray-100 px-4 py-3 flex items-center justify-between">
                <h4 className="font-semibold text-gray-800 text-sm">Aperçu du document</h4>
                <button 
                  onClick={() => setShowPDF(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <iframe
                src={pdfUrl}
                title="Disponibilités PDF"
                className="w-full h-[400px] md:h-[600px]"
              />
            </div>
          )}
        </div>

        {/* Payment Plan Section */}
        <div className={`mt-12 transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold mb-4 tracking-wide uppercase">
              💰 Plan de Financement
            </span>
            <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              <span className="gradient-text">Modalité de Paiement</span>
            </h3>
            <p className="text-sm md:text-base text-gray-600 max-w-xl mx-auto">
              Profitez d'un plan de paiement échelonné sur 18 mois
            </p>
          </div>

          {/* Desktop Payment Table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-6 py-4">
              <div className="grid grid-cols-4 gap-4 text-sm font-semibold uppercase tracking-wide">
                <div>Date</div>
                <div>Mois</div>
                <div>Étape</div>
                <div className="text-center">Pourcentage</div>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {[
                { date: '—', month: 'Mois 0', step: 'Réservation', percent: 5, highlight: false },
                { date: 'Mai 2026', month: 'Mois 1', step: 'Démarrage', percent: 30, highlight: true },
                { date: 'Sep 2026', month: 'Mois 4', step: 'Étage 2', percent: 30, highlight: false },
                { date: 'Janv 2027', month: 'Mois 7', step: 'Étage 5', percent: 20, highlight: false },
                { date: 'Mai 2027', month: 'Mois 9', step: 'Finition', percent: 10, highlight: false },
                { date: 'Sept 2027', month: 'Mois 18', step: 'Remise des Clés', percent: 5, highlight: true },
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`grid grid-cols-4 gap-4 px-6 py-4 items-center transition-colors duration-200 ${
                    item.highlight ? 'bg-amber-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-700">{item.date}</div>
                  <div className="text-gray-600 font-semibold">{item.month}</div>
                  <div className="text-gray-800">{item.step}</div>
                  <div className="text-center">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold ${
                      item.percent >= 30 ? 'bg-amber-100 text-amber-700' : 
                      item.percent >= 20 ? 'bg-blue-100 text-blue-700' : 
                      'bg-green-100 text-green-700'
                    }`}>
                      {item.percent}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
              <div className="grid grid-cols-4 gap-4 items-center">
                <div className="col-span-3 text-white font-bold text-lg">Total</div>
                <div className="text-center">
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-lg font-bold bg-white text-amber-600">
                    100%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Payment Cards */}
          <div className="md:hidden space-y-3">
            {[
              { date: '—', month: 'Mois 0', step: 'Réservation', percent: 5, icon: '📋' },
              { date: 'Mai 2026', month: 'Mois 1', step: 'Démarrage', percent: 30, icon: '🚀' },
              { date: 'Sep 2026', month: 'Mois 4', step: 'Étage 2', percent: 30, icon: '🏗️' },
              { date: 'Janv 2027', month: 'Mois 7', step: 'Étage 5', percent: 20, icon: '🏗️' },
              { date: 'Mai 2027', month: 'Mois 9', step: 'Finition', percent: 10, icon: '🏗️' },
              { date: 'Sept 2027', month: 'Mois 18', step: 'Remise des Clés', percent: 5, icon: '🔑' },
            ].map((item, index) => (
              <div 
                key={index}
                className={`bg-white rounded-xl p-4 shadow-md border transition-all duration-200 ${
                  index === 0 || index === 5 ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{item.icon}</span>
                    <span className="font-bold text-gray-800">{item.month}</span>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                    item.percent >= 30 ? 'bg-amber-100 text-amber-700' : 
                    item.percent >= 20 ? 'bg-blue-100 text-blue-700' : 
                    'bg-green-100 text-green-700'
                  }`}>
                    {item.percent}%
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-xs text-gray-500 mb-0.5">Date</div>
                    <div className="font-semibold text-gray-800">{item.date}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-xs text-gray-500 mb-0.5">Étape</div>
                    <div className="font-semibold text-gray-800 text-xs">{item.step}</div>
                  </div>
                </div>
              </div>
            ))}
            {/* Total Card */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <span className="text-white font-bold text-lg">Total</span>
                <span className="inline-flex items-center px-4 py-2 rounded-full text-lg font-bold bg-white text-amber-600">
                  100%
                </span>
              </div>
            </div>
          </div>

          {/* Progress Bar Visual */}
          <div className="mt-8 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <h4 className="text-center font-semibold text-gray-800 mb-4">Progression des Paiements</h4>
            <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden">
              <div className="absolute inset-y-0 left-0 w-[5%] bg-green-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold hidden sm:block">5%</span>
              </div>
              <div className="absolute inset-y-0 left-[5%] w-[30%] bg-amber-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">30%</span>
              </div>
              <div className="absolute inset-y-0 left-[35%] w-[30%] bg-blue-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">30%</span>
              </div>
              <div className="absolute inset-y-0 left-[65%] w-[20%] bg-purple-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">20%</span>
              </div>
              <div className="absolute inset-y-0 left-[85%] w-[10%] bg-pink-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold hidden sm:block">10%</span>
              </div>
              <div className="absolute inset-y-0 left-[95%] w-[5%] bg-green-600 flex items-center justify-center">
                <span className="text-white text-xs font-bold hidden sm:block">5%</span>
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>Réservation</span>
              <span>Démarrage</span>
              <span>Étage 2</span>
              <span>Étage 5</span>
              <span>Finition</span>
              <span>Remise des Clés</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RyadAvailability;
